import * as yup from 'yup';
import { Typography } from '@mui/material';
import { useMemo } from 'react';
import DatePickerField from 'components/forms/fields/date-picker-field/date-picker-field.component';
import DisplayInformationTitleField from 'components/forms/fields/display-information-field/display-information-supporting-fields/display-information-title-field/display-information-title-field.component';
import DisplayInformationDescriptionField from 'components/forms/fields/display-information-field/display-information-supporting-fields/display-information-description-field/display-information-description-field.component';
import DisplayInformationImageUploadField from 'components/forms/fields/display-information-field/display-information-supporting-fields/display-information-image-upload-field/display-information-image-upload-field.component';
import { ObjectType } from 'components/forms/fields/file-upload-field/file-upload-field.utils';
import { BulkEditGridColDef } from 'components/inc-data-grid/bulk-edit-data-grid.types';
import IncImageCell from 'components/inc-data-grid/cells/inc-image-cell/inc-image-cell.component';
import { useTranslation } from 'react-i18next';
import {
  getEnglishDisplayInfo,
  getEnglishDisplayInfoImages,
} from './menu-management-data-grid.utils';

type MenuManagementBulkEditFieldsOptions = {
  smallImageObjectType?: ObjectType;
};

export const useMenuManagementBulkEditFields = ({
  smallImageObjectType = 'CATALOG',
}: MenuManagementBulkEditFieldsOptions = {}) => {
  const { t } = useTranslation();

  // Memoized so the fields keep their identity across renders; the toolbar
  // hooks list them as useMemo deps.
  return useMemo(() => {
    const dateSchema = yup
      .string()
      .required()
      .default(null)
      .typeError(() => t('forms.errors.mixed.required'));
    // Bulk edit sets a single image; the row's image list is pre-filled with
    // its first image and sent back as a list (see toBulkUpdateValue).
    const imageSchema = yup
      .string()
      .transform((value, originalValue) =>
        Array.isArray(originalValue) ? originalValue[0] ?? '' : value,
      )
      .singleImage()
      .default('');

    return {
      status: {
        schema: yup.object({
          status: yup.string().required().default('Active'),
        }),
      },
      extendedAttributes: {
        schema: yup.object({
          extendedAttributes: yup.array().required().default([]),
        }),
      },
      displayRank: {
        schema: yup.object({
          displayRank: yup.number().required().min(0).default(1),
        }),
      },
      startDate: {
        schema: yup.object({
          startDate: dateSchema,
        }),
        FormComponent: (
          <DatePickerField
            name="startDate"
            label={t('MenusDataGrid.column.startDate.label')}
            formatValue={value => value?.toISODate()}
          />
        ),
      },
      endDate: {
        schema: yup.object({
          endDate: dateSchema,
        }),
        FormComponent: (
          <DatePickerField
            name="endDate"
            label={t('MenusDataGrid.column.endDate.label')}
            formatValue={value => value?.toISODate()}
          />
        ),
      },
      price: {
        schema: yup.object({
          price: yup.number().required().min(0),
        }),
      },
      itemType: {
        schema: yup.object({
          itemType: yup.string().required().default(''),
        }),
      },
      inventory: {
        schema: yup.object({
          inventory: yup.string().required().default(''),
        }),
      },
      // Always used as a set, so they're spread into each grid's fields.
      displayInfo: {
        displayTitle: {
          schema: yup.object({
            displayTitle: yup.string().trim().max(100).required().default(''),
          }),
          FormComponent: (
            <DisplayInformationTitleField
              name="displayTitle"
              label={t('MenuManagementDataGrid.column.displayTitle.label')}
              data-testid="MenuManagementBulkEdit.DisplayTitle"
            />
          ),
        },
        shortDescription: {
          schema: yup.object({
            shortDescription: yup.string().max(500).default(''),
          }),
          FormComponent: (
            <DisplayInformationDescriptionField
              name="shortDescription"
              label={t('MenuManagementDataGrid.column.shortDescription.label')}
              data-testid="MenuManagementBulkEdit.ShortDescription"
            />
          ),
        },
        longDescription: {
          schema: yup.object({
            longDescription: yup.string().max(1000).default(''),
          }),
          FormComponent: (
            <DisplayInformationDescriptionField
              name="longDescription"
              label={t('MenuManagementDataGrid.column.longDescription.label')}
              data-testid="MenuManagementBulkEdit.LongDescription"
            />
          ),
        },
        smallImage: {
          schema: yup.object({
            smallImage: imageSchema,
          }),
          FormComponent: (
            <DisplayInformationImageUploadField
              name="smallImage"
              label={t('MenuManagementDataGrid.column.smallImage.label')}
              data-testid="MenuManagementBulkEdit.SmallImage"
              objectType={smallImageObjectType}
            />
          ),
        },
        mediumImage: {
          schema: yup.object({
            mediumImage: imageSchema,
          }),
          FormComponent: (
            <DisplayInformationImageUploadField
              name="mediumImage"
              label={t('MenuManagementDataGrid.column.mediumImage.label')}
              data-testid="MenuManagementBulkEdit.MediumImage"
              objectType="CATALOG"
            />
          ),
        },
      },
    };
  }, [t, smallImageObjectType]);
};

/**
 * English display info columns shared by the menus, groups and items grids.
 * Values are read from the row's EN `displayInfo` entry, so they are not
 * sortable on the server.
 */
export const useMenuManagementDisplayInfoColumns = (): BulkEditGridColDef[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        field: 'displayTitle',
        headerName: t('MenuManagementDataGrid.column.displayTitle.label'),
        minWidth: 250,
        sortable: false,
        display: 'flex',
        valueGetter: (_value, row) => getEnglishDisplayInfo(row)?.title ?? '',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'shortDescription',
        headerName: t('MenuManagementDataGrid.column.shortDescription.label'),
        minWidth: 300,
        sortable: false,
        display: 'flex',
        valueGetter: (_value, row) =>
          getEnglishDisplayInfo(row)?.shortDescription ?? '',
        renderCell: params => (
          <Typography noWrap title={params.value}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'longDescription',
        headerName: t('MenuManagementDataGrid.column.longDescription.label'),
        minWidth: 300,
        sortable: false,
        display: 'flex',
        valueGetter: (_value, row) =>
          getEnglishDisplayInfo(row)?.longDescription ?? '',
        renderCell: params => (
          <Typography noWrap title={params.value}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'smallImage',
        headerName: t('MenuManagementDataGrid.column.smallImage.label'),
        minWidth: 120,
        sortable: false,
        display: 'flex',
        valueGetter: (_value, row) =>
          getEnglishDisplayInfoImages(row, 'smallImage'),
        renderCell: params => <IncImageCell value={params.value} />,
      },
      {
        field: 'mediumImage',
        headerName: t('MenuManagementDataGrid.column.mediumImage.label'),
        minWidth: 120,
        sortable: false,
        display: 'flex',
        valueGetter: (_value, row) =>
          getEnglishDisplayInfoImages(row, 'mediumImage'),
        renderCell: params => <IncImageCell value={params.value} />,
      },
    ],
    [t],
  );
};

export const displayInfoColumnVisibilityModel = {
  displayTitle: false,
  shortDescription: false,
  longDescription: false,
  smallImage: false,
  mediumImage: false,
};
